import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

// ─── Default departments (mirrors src/components/DepartmentManager.js) ───
// These are the canonical 19 MA-plan departments with the alias lists Mandate
// uses for memo classification. The org admin can uncheck or edit any of them.
const DEFAULT_DEPARTMENTS = [
  { name: 'Case Management', aliases: ['Case Management', 'Complex Case Management', 'Health Case Management', 'Care Coordination', 'Coordinated Care', 'Case Oversight'] },
  { name: 'Claims', aliases: ['Claims Processing', 'Claims', 'Claims Adjudication', 'Claims Operations', 'Claims Dept', 'Claims Review', 'Part C Claims'] },
  { name: 'Compliance', aliases: ['Compliance', 'Regulatory Affairs', 'Reg Affairs', 'Reg & Compliance', 'Audit & Compliance', 'Regulatory Oversight', 'CPE', 'ODAG', 'CDAG', 'FA', 'SNPCC'] },
  { name: 'Special Investigations & Data Analytics (SIU)', aliases: ['Special Investigations Unit', 'SIU', 'Fraud, Waste & Abuse', 'FWA', 'Fraud Investigation', 'Payment Integrity', 'PI', 'Program Integrity', 'Data Analytics', 'Analytics', 'Business Intelligence'] },
  { name: 'Enrollment', aliases: ['Eligibility & Enrollment', 'ENRL', 'Eligibility', 'Out of Area', 'Retroactive', 'RPC', 'SCC', 'LEP', 'Late Enrollment Penalty', 'BAE', 'Best Available Evidence'] },
  { name: 'Finance & Accounting', aliases: ['Finance', 'Accounting', 'Financial Operations', 'Premium Billing', 'Capitation', 'MLR', 'Medical Loss Ratio', 'Bid Pricing', 'Rate Development'] },
  { name: 'Grievances & Appeals', aliases: ['Grievances', 'Appeals', 'Member Appeals', 'Grievance & Appeals', 'IRE', 'QIO', 'Maximus'] },
  { name: 'Human Resources', aliases: ['Human Resources', 'HR', 'People Ops', 'Talent Management', 'Employee Relations'] },
  { name: 'Information Technology', aliases: ['Information Technology', 'IT', 'Systems', 'Tech Ops', 'Infrastructure'] },
  { name: 'Legal', aliases: ['Legal', 'General Counsel', 'GC', 'Legal Affairs', 'Law Dept'] },
  { name: 'Marketing & Communications', aliases: ['Marketing', 'Communications', 'TPMO', 'ANOC', 'EOC', 'SB', 'Summary of Benefits', 'Member Materials', 'Marketing Compliance', 'File & Use'] },
  { name: 'Member Services', aliases: ['Member Services', 'Member Support', 'Customer Care', 'CSR'] },
  { name: 'Pharmacy', aliases: ['Pharmacy Benefits Management', 'PBM', 'Drug Benefits', 'Rx Benefits', 'Pharmacy Operations', 'Step Therapy', 'Quantity Limit', 'ST', 'QL'] },
  { name: 'Provider Relations', aliases: ['Provider Network', 'Provider Relations', 'Network Management', 'Provider Services', 'Provider Contracting'] },
  { name: 'Quality', aliases: ['Quality Improvement', 'QI', 'Quality', 'HEDIS', 'Stars Quality'] },
  { name: 'Risk Adjustment', aliases: ['Risk Adjustment', 'RAF', 'Revenue Risk', 'Risk Coding', 'Risk Analytics'] },
  { name: 'Sales', aliases: ['Sales', 'Broker Services', 'Agent Services', 'Broker Channel'] },
  { name: 'Utilization Management', aliases: ['Utilization Management', 'UM', 'Prior Authorizations', 'Utilization Review', 'PA Review'] },
  { name: 'HEDIS', aliases: ['HEDIS', 'Healthcare Effectiveness Data and Information Set', 'HEDIS Measures', 'Quality Measures', 'NCQA', 'CAHPS', 'HOS'] },
];

// 2026-08-24 role redesign: super users (org_superadmin) are assigned by the
// platform super admin only, so the wizard does not offer that type. At least
// one Compliance Officer is required before setup can finish — the super user
// manages access; the compliance officer works the compliance apps.
const USER_TYPES = [
  { key: 'compliance_officer', label: 'Compliance Officer', desc: 'Org-wide compliance lead — creates memos, CAPs, policies' },
  { key: 'compliance_analyst', label: 'Compliance Analyst', desc: 'Org-wide compliance team — write risks/audits/issues, no memos/CAPs' },
  { key: 'department_lead',    label: 'Department Lead',    desc: 'Head of one department — full control of dept data' },
  { key: 'department_user',    label: 'Department User',    desc: 'Frontline staff in one department' },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Step 1+2: department selection + aliases
  const [deptSelections, setDeptSelections] = useState(() =>
    DEFAULT_DEPARTMENTS.map(d => ({ ...d, selected: true, customAliases: [...d.aliases] }))
  );
  const [createdDepts, setCreatedDepts] = useState([]); // populated after step 2 saves

  // Step 3: contracts (skipped if memo + cap not purchased)
  const [contracts, setContracts] = useState([]);
  const [newContractName, setNewContractName] = useState('');
  const [newContractPlanId, setNewContractPlanId] = useState('');

  // Step 4: team members
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberType, setNewMemberType] = useState('department_user');
  const [newMemberDeptId, setNewMemberDeptId] = useState('');
  const [newMemberAlsoDeptIds, setNewMemberAlsoDeptIds] = useState([]); // "also oversees"

  // ─── Initial session check ───
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/session`, { credentials: 'include' });
        const data = await res.json();
        if (!data.success) {
          navigate('/login');
          return;
        }
        const u = data.user;
        const wizUt = u.user_type || u.effective_user_type || null;
        const isOrgAdmin = wizUt ? wizUt === 'org_superadmin' : u.role === 'superuser';
        if (!isOrgAdmin) {
          navigate('/app');
          return;
        }
        // The wizard is re-enterable: an org admin can reopen it any time from
        // the sidebar to adjust departments, aliases, contracts, or team, even
        // after first-time setup is complete (department creation is idempotent).
        // Access is still gated to org admins by the isOrgAdmin check above.
        setUser(u);
      } catch (err) {
        navigate('/login');
      }
      setLoading(false);
    })();
  }, [navigate]);

  // Whether to show step 3 (contracts) — only if Mandate or CAP is purchased
  const showContractStep =
    user?.organization_products?.memo === true ||
    user?.organization_products?.cap === true;

  // Steps: 1=Departments, 2=Aliases, [3=Contracts?], 4=Team
  const totalSteps = 2
    + (showContractStep ? 1 : 0)
    + 1 /* Team */;

  if (loading) return <FullPageSpinner label="Loading…" />;
  if (!user) return null;

  // ─── Step handlers ───
  const handleStep1Continue = () => {
    setError(null);
    if (deptSelections.filter(d => d.selected).length === 0) {
      setError('Please select at least one department.');
      return;
    }
    setStep(2);
  };

  const handleStep2Save = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const toCreate = deptSelections
        .filter(d => d.selected)
        .map(d => ({ name: d.name, aliases: d.customAliases }));

      const res = await fetch(`${API}/api/admin/v2/orgs/${user.organization_id}/departments/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ departments: toCreate }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to save departments');
        setSubmitting(false);
        return;
      }

      // Refetch the full department list (newly created + any pre-existing)
      const listRes = await fetch(`${API}/api/admin/v2/orgs/${user.organization_id}/departments`, { credentials: 'include' });
      const listData = await listRes.json();
      if (listData.success) {
        setCreatedDepts(listData.departments || []);
      }
      setStep(showContractStep ? 3 : 4);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  const handleAddContract = async () => {
    setError(null);
    if (!newContractName.trim()) return;

    // The server requires a Plan ID and rejects the contract without one. Say
    // so here rather than sending a request that is guaranteed to fail.
    if (!newContractPlanId.trim()) {
      setError('Plan ID (PBP) is required. Department users cannot be set up in Mandate without it.');
      return;
    }

    try {
      const res = await fetch(`${API}/api/contracts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newContractName, plan_id: newContractPlanId }),
      });
      const data = await res.json().catch(() => ({}));

      // Fail closed. This previously accepted anything that was not an explicit
      // `success: false`, so a 500, a timeout, or an unexpected body left the
      // contract listed on screen while nothing was saved. A whole client was
      // set up that way and every department user was silently rejected by
      // Mandate for months afterwards.
      if (!res.ok || data.success !== true) {
        setError(
          `Contract NOT saved: ${data.message || `server returned ${res.status}`}. `
          + `Do not continue — department users cannot reach Mandate without a saved contract.`
        );
        return;
      }

      // memo_synced retired 2026-08-12: the standalone Mandate app kept its own
      // contracts table and a contract had to be pushed to it over HTTP.
      // Precision Mandate routes on this contract directly, so a saved contract
      // is a routable contract and there is no second state to warn about.
      setContracts(prev => [...prev, {
        name: newContractName,
        plan_id: newContractPlanId,
      }]);
      setNewContractName('');
      setNewContractPlanId('');
    } catch (err) {
      setError('Failed to add contract: ' + err.message + ' — the contract was NOT saved.');
    }
  };

  const handleAddMember = () => {
    setError(null);
    if (!newMemberName.trim() || !newMemberEmail.trim()) {
      setError('Name and email are required');
      return;
    }
    const requiresDept = ['department_lead', 'department_user'].includes(newMemberType);
    if (requiresDept && !newMemberDeptId) {
      setError('Department is required for this user type');
      return;
    }
    // Department only for department-bound types. A department picked
    // earlier in the form must never ride along on a compliance-tier user:
    // in Mandate, a department id on a compliance user hides memo upload.
    const alsoIds = requiresDept ? newMemberAlsoDeptIds.filter(d => d !== newMemberDeptId) : [];
    setTeamMembers(prev => [...prev, {
      name: newMemberName,
      email: newMemberEmail,
      user_type: newMemberType,
      department_id: requiresDept ? (newMemberDeptId || null) : null,
      department_name: requiresDept ? (createdDepts.find(d => d.id === newMemberDeptId)?.name || null) : null,
      additional_department_ids: alsoIds,
      also_oversees_names: alsoIds.map(id => createdDepts.find(d => d.id === id)?.name).filter(Boolean),
    }]);
    setNewMemberName('');
    setNewMemberEmail('');
    setNewMemberDeptId('');
    setNewMemberAlsoDeptIds([]);
  };

  const handleRemoveMember = (idx) => {
    setTeamMembers(prev => prev.filter((_, i) => i !== idx));
  };

  // Team step — create all team members, mark setup complete, then enter the app.
  const handleTeamContinue = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const created = [];
      for (const m of teamMembers) {
        const res = await fetch(`${API}/api/admin/v2/orgs/${user.organization_id}/users`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: m.name,
            email: m.email,
            user_type: m.user_type,
            department_id: m.department_id,
            additional_department_ids: m.additional_department_ids || [],
            send_invite: true,
          }),
        });
        const data = await res.json();
        if (data.success) {
          created.push({ ...m, user_id: data.user?.id || data.user_id || null });
        }
      }
      if (created.length > 0) setTeamMembers(created);

      // Mark setup complete. The server refuses until the organization has
      // at least one active Compliance Officer — surface that message here
      // instead of navigating away.
      const doneRes = await fetch(`${API}/api/admin/v2/orgs/${user.organization_id}/setup-complete`, {
        method: 'POST',
        credentials: 'include',
      });
      const doneData = await doneRes.json().catch(() => ({}));
      if (!doneRes.ok || doneData.success === false) {
        setError(doneData.message || 'Setup could not be completed.');
        setSubmitting(false);
        return;
      }

      navigate('/people');
    } catch (err) {
      setError('Failed to finish setup: ' + err.message);
      setSubmitting(false);
    }
  };

  // ─── UI ───
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-slate-900 font-extrabold text-[10px] tracking-[0.15em]">PCG</span>
              </div>
              <div>
                <h1 className="text-lg font-bold">Welcome to {user.organization_name}</h1>
                <p className="text-xs text-slate-500">Let's set up your organization in {totalSteps} quick steps</p>
              </div>
            </div>
            <div className="text-xs text-slate-500">{user.email}</div>
          </div>
          <ProgressBar
            currentIndex={
              step === 1 ? 1 :
              step === 2 ? 2 :
              step === 3 ? 3 :
              step === 4 ? (showContractStep ? 4 : 3) :
              1
            }
            totalSteps={totalSteps}
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {step === 1 && (
          <Step1Departments
            deptSelections={deptSelections}
            setDeptSelections={setDeptSelections}
            onContinue={handleStep1Continue}
          />
        )}

        {step === 2 && (
          <Step2Aliases
            deptSelections={deptSelections}
            setDeptSelections={setDeptSelections}
            onBack={() => setStep(1)}
            onSave={handleStep2Save}
            submitting={submitting}
          />
        )}

        {step === 3 && showContractStep && (
          <Step3Contracts
            contracts={contracts}
            newContractName={newContractName}
            setNewContractName={setNewContractName}
            newContractPlanId={newContractPlanId}
            setNewContractPlanId={setNewContractPlanId}
            onAdd={handleAddContract}
            onBack={() => setStep(2)}
            onContinue={() => setStep(4)}
          />
        )}

        {step === 4 && (
          <Step4Team
            createdDepts={createdDepts}
            teamMembers={teamMembers}
            newMemberName={newMemberName}
            setNewMemberName={setNewMemberName}
            newMemberEmail={newMemberEmail}
            setNewMemberEmail={setNewMemberEmail}
            newMemberType={newMemberType}
            setNewMemberType={setNewMemberType}
            newMemberDeptId={newMemberDeptId}
            newMemberAlsoDeptIds={newMemberAlsoDeptIds}
            setNewMemberAlsoDeptIds={setNewMemberAlsoDeptIds}
            setNewMemberDeptId={setNewMemberDeptId}
            onAdd={handleAddMember}
            onRemove={handleRemoveMember}
            onBack={() => setStep(showContractStep ? 3 : 2)}
            onFinish={handleTeamContinue}
            submitting={submitting}
            stepNumber={showContractStep ? 4 : 3}
          />
        )}
      </main>
    </div>
  );
}

// ────────────────────────── Step 1: Department selection ──────────────────────────
function Step1Departments({ deptSelections, setDeptSelections, onContinue }) {
  const [newDeptName, setNewDeptName] = useState('');
  const toggleAll = (selected) => {
    setDeptSelections(prev => prev.map(d => ({ ...d, selected })));
  };
  const toggle = (idx) => {
    setDeptSelections(prev => prev.map((d, i) => i === idx ? { ...d, selected: !d.selected } : d));
  };
  const addCustomDept = () => {
    const name = newDeptName.trim();
    if (!name) return;
    // Case-insensitive duplicate guard against defaults and prior customs
    if (deptSelections.some(d => d.name.toLowerCase() === name.toLowerCase())) {
      setNewDeptName('');
      return;
    }
    // Custom departments join the same pipeline as the defaults: alias
    // editing in Step 2, then the same bulk create + Mandate sync on save.
    setDeptSelections(prev => [...prev, { name, aliases: [], selected: true, customAliases: [name], custom: true }]);
    setNewDeptName('');
  };
  const selectedCount = deptSelections.filter(d => d.selected).length;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <SectionHeader number="1" title="Confirm your departments" />
      <p className="text-sm text-slate-600 mb-4">
        We've pre-loaded the {DEFAULT_DEPARTMENTS.length} standard departments for Medicare Advantage plans. Uncheck any you don't need, and add any departments specific to your organization below.
      </p>

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">{selectedCount} of {deptSelections.length} selected</div>
        <div className="flex gap-2">
          <button onClick={() => toggleAll(true)} className="text-xs text-blue-400 hover:text-blue-300">Select all</button>
          <span className="text-slate-700">·</span>
          <button onClick={() => toggleAll(false)} className="text-xs text-slate-500 hover:text-slate-700">Clear all</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
        {deptSelections.map((dept, idx) => (
          <label key={dept.name} className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition ${
            dept.selected ? 'bg-blue-500/10 border-blue-500/40' : 'bg-white border-slate-200 hover:border-slate-400'
          }`}>
            <input
              type="checkbox"
              checked={dept.selected}
              onChange={() => toggle(idx)}
              className="mt-0.5 rounded border-slate-300 bg-white text-blue-500 focus:ring-blue-500"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900">{dept.name}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{dept.custom ? 'Custom department' : `${dept.aliases.length} default aliases`}</p>
            </div>
          </label>
        ))}
      </div>

      <div className="mb-6 p-3 rounded-lg border border-dashed border-slate-300 bg-slate-100">
        <p className="text-xs font-medium text-slate-700 mb-2">Add a department we didn't list</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={newDeptName}
            onChange={(e) => setNewDeptName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomDept(); } }}
            placeholder="e.g. Stars, Delegation Oversight, Model of Care"
            className="flex-1 px-3 py-2 rounded-lg bg-white border border-slate-300 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={addCustomDept}
            disabled={!newDeptName.trim()}
            className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-800 hover:border-blue-500 hover:text-slate-900 disabled:opacity-40 transition"
          >
            Add
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">You'll set its aliases in the next step, the same as every other department.</p>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onContinue}
          disabled={selectedCount === 0}
          className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition"
        >
          Continue to Aliases →
        </button>
      </div>
    </div>
  );
}

// ────────────────────────── Step 2: Aliases per department ──────────────────────────
function Step2Aliases({ deptSelections, setDeptSelections, onBack, onSave, submitting }) {
  const updateAlias = (deptIdx, aliases) => {
    setDeptSelections(prev => prev.map((d, i) => i === deptIdx ? { ...d, customAliases: aliases } : d));
  };
  const selected = deptSelections.filter(d => d.selected);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <SectionHeader number="2" title="Set up department aliases" />
      <p className="text-sm text-slate-600 mb-2">
        These aliases are what the AI looks for when classifying memos and audit findings to assign them to your team.
      </p>
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-3 mb-4 text-xs text-blue-300">
        <strong>💡 Why this matters:</strong> If your "Compliance" team sometimes goes by "Reg Affairs" or handles "ODAG/CDAG," add those terms as aliases. When a memo arrives titled "CDAG Decision Letter Update," it'll auto-route to Compliance instead of being unclassified.
      </div>

      <div className="space-y-3 mb-6">
        {deptSelections.map((dept, idx) => {
          if (!dept.selected) return null;
          return (
            <div key={dept.name} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-900">{dept.name}</h4>
                <span className="text-[11px] text-slate-500">{dept.customAliases.length} aliases</span>
              </div>
              <AliasTagInput
                aliases={dept.customAliases}
                onChange={(aliases) => updateAlias(idx, aliases)}
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm transition">
          ← Back
        </button>
        <button
          onClick={onSave}
          disabled={submitting}
          className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-sm font-medium disabled:opacity-50 hover:shadow-lg hover:shadow-blue-500/25 transition"
        >
          {submitting ? 'Saving…' : 'Save Departments & Continue →'}
        </button>
      </div>
    </div>
  );
}

function AliasTagInput({ aliases, onChange }) {
  const [input, setInput] = useState('');
  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !aliases.includes(trimmed)) {
      onChange([...aliases, trimmed]);
      setInput('');
    }
  };
  const remove = (alias) => onChange(aliases.filter(a => a !== alias));
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {aliases.map(alias => (
          <span key={alias} className="inline-flex items-center gap-1.5 bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs px-2.5 py-1 rounded-full">
            {alias}
            <button onClick={() => remove(alias)} className="text-blue-400 hover:text-blue-200 font-bold">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add an alias (e.g. ODAG, Reg Affairs)"
          className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
        />
        <button onClick={add} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-900">Add</button>
      </div>
    </div>
  );
}

// ────────────────────────── Step 3: Contracts (only if memo or cap purchased) ──────────────────────────
function Step3Contracts({ contracts, newContractName, setNewContractName, newContractPlanId, setNewContractPlanId, onAdd, onBack, onContinue }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <SectionHeader number="3" title="Add your contracts (optional)" />
      <p className="text-sm text-slate-600 mb-4">
        If your organization holds CMS contracts (e.g. H1234), add them here. You can also do this later from the Contracts page.
      </p>

      {contracts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg mb-4">
          {contracts.map((c, idx) => (
            <div key={idx} className="px-4 py-3 border-b border-slate-200 last:border-b-0 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-900 font-medium">{c.name}</p>
                {c.plan_id && <p className="text-xs text-slate-500">Plan ID: {c.plan_id}</p>}
              </div>
              <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-full border border-emerald-500/20">Added</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-4">
        <input
          value={newContractName}
          onChange={e => setNewContractName(e.target.value)}
          placeholder="Contract name (e.g. H1234)"
          className="bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
        />
        <input
          value={newContractPlanId}
          onChange={e => setNewContractPlanId(e.target.value)}
          placeholder="Plan name (optional)"
          className="bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
        />
      </div>
      <button
        onClick={onAdd}
        disabled={!newContractName.trim()}
        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg text-xs font-medium text-slate-900 mb-6"
      >
        + Add Contract
      </button>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm transition">← Back</button>
        <button
          onClick={onContinue}
          className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-blue-500/25 transition"
        >
          Continue to Team →
        </button>
      </div>
    </div>
  );
}

// ────────────────────────── Step 4: First team members ──────────────────────────
function Step4Team({ createdDepts, teamMembers, newMemberName, setNewMemberName, newMemberEmail, setNewMemberEmail, newMemberType, setNewMemberType, newMemberDeptId, setNewMemberDeptId, newMemberAlsoDeptIds = [], setNewMemberAlsoDeptIds = () => {}, onAdd, onRemove, onBack, onFinish, submitting, stepNumber = 4, finishLabel = '✓ Finish & Send Invites' }) {
  const requiresDept = ['department_lead', 'department_user'].includes(newMemberType);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <SectionHeader number={String(stepNumber)} title="Add your first team members" />
      <p className="text-sm text-slate-600 mb-4">
        Each person you add will receive a welcome email with a link to set their password. You can always add more later from the People page.
      </p>

      {teamMembers.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg mb-4 overflow-hidden">
          {teamMembers.map((m, idx) => {
            const typeLabel = USER_TYPES.find(t => t.key === m.user_type)?.label || m.user_type;
            return (
              <div key={idx} className="px-4 py-3 border-b border-slate-200 last:border-b-0 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-slate-900 font-medium truncate">{m.name}</p>
                    <span className="text-[10px] bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full border border-blue-500/30">{typeLabel}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {m.email}{m.department_name ? ` · ${m.department_name}` : ''}
                    {(m.also_oversees_names || []).length > 0 ? ` · also oversees ${m.also_oversees_names.join(', ')}` : ''}
                  </p>
                </div>
                <button onClick={() => onRemove(idx)} className="text-slate-500 hover:text-red-400 text-lg ml-3">×</button>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 mb-4">
        <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-3">Add a person</p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <input
            value={newMemberName}
            onChange={e => setNewMemberName(e.target.value)}
            placeholder="Full name"
            className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
          />
          <input
            type="email"
            value={newMemberEmail}
            onChange={e => setNewMemberEmail(e.target.value)}
            placeholder="email@example.com"
            className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <select
            value={newMemberType}
            onChange={e => {
              setNewMemberType(e.target.value);
              // Switching to an org-wide type discards any previously picked
              // department so it can't silently ride along on the new member.
              if (!['department_lead', 'department_user'].includes(e.target.value)) setNewMemberDeptId('');
            }}
            className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
          >
            {USER_TYPES.map(t => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
          {requiresDept ? (
            <select
              value={newMemberDeptId}
              onChange={e => setNewMemberDeptId(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Select department…</option>
              {createdDepts.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          ) : (
            <div className="px-4 py-2 text-xs text-slate-500 italic">No dept needed for this type</div>
          )}
        </div>

        {requiresDept && newMemberDeptId && (
          <div className="mb-3">
            <p className="text-xs text-slate-600 mb-1.5">
              Also oversees (optional) — extra departments this person sees across the platform, for someone supervising several areas. Role and primary department stay as chosen above.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {createdDepts.filter(d => d.id !== newMemberDeptId).map(d => {
                const on = newMemberAlsoDeptIds.includes(d.id);
                return (
                  <button key={d.id} type="button"
                    onClick={() => setNewMemberAlsoDeptIds(prev => on ? prev.filter(x => x !== d.id) : [...prev, d.id])}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      on ? 'bg-blue-500/20 border-blue-400 text-blue-200' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
                    }`}>
                    {d.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-500 mb-3">
          {USER_TYPES.find(t => t.key === newMemberType)?.desc}
        </p>

        <button
          onClick={onAdd}
          disabled={!newMemberName.trim() || !newMemberEmail.trim()}
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg text-xs font-medium text-slate-900"
        >
          + Add to team
        </button>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm transition">← Back</button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">{teamMembers.length} team member{teamMembers.length !== 1 ? 's' : ''} ready to invite</span>
          <button
            onClick={onFinish}
            disabled={submitting}
            className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-lg text-sm font-semibold disabled:opacity-50 hover:shadow-lg hover:shadow-emerald-500/25 transition"
          >
            {submitting ? 'Setting up…' : finishLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────── Helpers ──────────────────────────
function SectionHeader({ number, title }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-400 text-sm font-bold flex items-center justify-center">
        {number}
      </div>
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    </div>
  );
}

function ProgressBar({ currentIndex, totalSteps }) {
  return (
    <div className="flex items-center gap-2" role="progressbar" aria-valuemin={1} aria-valuemax={totalSteps} aria-valuenow={currentIndex}>
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map(i => (
        <div key={i} className={`flex-1 h-1.5 rounded-full transition-all ${
          i <= currentIndex ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-slate-100'
        }`} />
      ))}
    </div>
  );
}

function FullPageSpinner({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-600 text-sm">{label}</p>
      </div>
    </div>
  );
}
